/* eslint-disable no-template-curly-in-string */
/* eslint-disable react-hooks/exhaustive-deps */
//import packages
import React, { useState, useEffect } from "react";
import {
  Modal,
  Form,
  Row,
  Col,
  Spin,
  message,
  Upload,
  Input,
  Select,
  Button,
  Tabs,
  Switch
} from "antd";
import { EditOutlined, LoadingOutlined, PlusOutlined } from "@ant-design/icons";
import styled from "styled-components";
import { HugeRTEWrapper } from '../HugeRTEWrapper';

//import API functions
import {
  getPhysicianDetails,
  physicianProfileUpdate,
} from "../../api/PhysiciansAPI";
import { dateOfBirthFormatter } from "../../utils/dateFormatter";
import authorization from "../../auth/authorization";

// import components
import { StyledAvatarWrapperColumn } from "./PhysicianDetailsComponent";
import ImageCropperModal from '../ImageCropperModal';
import PhysicianAvailability from "./PhysicianAvailability";
import PhysicianQueueConfigComponent from "./PhysicianQueueConfigComponent"

const StyledUpload = styled(Upload)`
  > .ant-upload-select-picture-card {
    width: 150px;
    height: 150px;
    margin: 20px auto 20px 2.5%;
    border-radius: 50%;
  }
  > .ant-upload-select-picture-card > .ant-upload {
    padding: 5px;
  }
  @media (max-width: 450px) {
    > .ant-upload-select-picture-card {
      width: 120px;
      height: 120px;
      margin: 20px auto 20px 2.5%;
    }
  }
`;

const StyledProfilePicInnerSection = styled.div`
  position: relative;
`;

const StyledProfilePicEditSection = styled.div`
  position: absolute;
  right: 10px;
  bottom: 0;
  color: #fff;
  background-color: #7bad7b;
  padding: 10%;
  border-radius: 25%;
`;

const StyledPhysicianEditModal = styled(Modal)`
  .ant-modal-body,
  .ant-spin-nested-loading,
  .ant-spin-container,
  .ant-tabs,
  form,
  .ant-row {
    max-width: 100%;
    min-width: 0;
  }

  .ant-modal-body {
    box-sizing: border-box;
  }
`;

const PhysicianDetails = ({
  physicianId,
  viewModal,
  closeModal,
  clients,
  states,
  tags,
  employmentStatus,
  telemedicineCategory,
  doctorCredentials,
  userType,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [imageLoading, setImageLoading] = useState(false);
  const [physician, setPhysician] = useState(null)
  const [profilePic, setProfilePic] = useState(null);
  const [updatePic, setUpdatePic] = useState(null);
  const [isCropping, setIsCropping] = useState(false);
  const [croppingImgSrc, setCroppingImgSrc] = useState();
  const [activeTabKey, setActiveTabKey] = useState("1");

  const layout = {
    labelCol: { span: 12 },
    wrapperCol: { span: 24 },
  };

  const validateMessages = { required: "${label} is required!" };

  const handleModalClose = () => closeModal(false);

  const fetchPhysicianDetails = async () => {
    try {
      setLoading(true);
      setImageLoading(true);

      const { data } = await getPhysicianDetails(physicianId)

      if (!data?.success) {
        return message.error("Unable to load Physician Details");
      }

      const { payload } = data;
      setPhysician(payload)

      if (payload.DOB) {
        payload.DOB = dateOfBirthFormatter(payload.DOB)
      }

      if (payload.state) {
        const stateId = states.find(eachState => eachState.name.toLowerCase() === payload.state.toLowerCase())._id;
        payload.state = stateId
      }

      if (payload.licensedStates) {
        payload.licensedStates = Array.from(new Set(payload.licensedStates.map(state => state._id)))
      }

      if (payload.teleMedicineCategoryIds) {
        payload.teleMedicineCategories = Array.from(
          new Set(payload.teleMedicineCategoryIds)
        );
      }

      if (payload.clientIds) {
        payload.clients = Array.from(new Set(payload.clientIds))
      }

      if (payload.stateIds) {
        payload.states = Array.from(new Set(payload.stateIds))
      }

      if (payload.profilePic1) {
        setProfilePic(`${payload.profilePic1}?v=${Date.now()}`);
      }

      form.setFieldsValue(payload)
    } catch {
      message.error("Unable to load Physician Details")
    } finally {
      setLoading(false)
      setImageLoading(false)
    };
  };

  const getBase64 = (img, callback) => {
    const reader = new FileReader();
    reader.addEventListener("load", () => {
      return callback(reader.result);
    });
    reader.readAsDataURL(img);
  };

  const beforeUpload = (file) => {
    const isJpgOrPng = file.type === "image/jpeg" || file.type === "image/png";
    if (!isJpgOrPng) {
      message.error("You can only upload JPG/PNG file!");
    }
    const isLt2M = file.size / 1024 / 1024 < 2;
    if (!isLt2M) {
      message.error("Image must smaller than 2MB!");
    }
    return isJpgOrPng && isLt2M;
  };

  const handleChange = (info) => {
    if (info.file.status === "uploading") {
      setImageLoading(true);
      return;
    }

    if (info.file.status === "done") {
      // Get this url from response in real world.
      getBase64(info.file.originFileObj, (profileUrl) => {
        setCroppingImgSrc(profileUrl);
        setIsCropping(true);
        setImageLoading(false);
      });
    }
  };

  const onSavingChanges = async (values) => {
    try {
      setLoading(true);

      const formValues = new FormData();

      for (let [k, v] of Object.entries(values)) {
        formValues.append(k, v);
      }

      formValues.append("physicianId", physicianId);
      formValues.append("DOB", new Date(values.DOB).toISOString());

      if (!values.zoomUserId) {
        formValues.delete("zoomUserId")
      }

      if (values.active === physician.active) {
        formValues.delete("active")
      }

      const state = states.find(eachState => eachState._id === values.state)?.name;
      formValues.set("state", state);

      if (updatePic) {
        formValues.append("profilePic1", updatePic);
      }

      const { data } = await physicianProfileUpdate(formValues)

      if (!data?.success) {
        const errorMessage = data?.message || "Unable to update physician details. Please try again!";
        return message.error(errorMessage);
      }

      message.success("Successfully Updated Physician Details");
      handleModalClose()
    } catch (error) {
      console.log(error);
      const errorMessage = error?.response?.data?.message || "Unable to update physician details. Please try again!";
      message.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const returnToFirstTab = () => {
    setActiveTabKey("1");
  }

  useEffect(() => {
    fetchPhysicianDetails(physicianId)
  }, []);

  return (
    <StyledPhysicianEditModal
      title="Edit Physician"
      open={viewModal}
      footer={
        activeTabKey === "1"
          ? [
              <Button
                key="save"
                id="physician-edit-save-btn"
                type="primary"
                size="large"
                loading={loading}
                onClick={() => form.submit()}
              >
                Save Changes
              </Button>,
            ]
          : null
      }
      onCancel={handleModalClose}
      width="min(1200px, calc(100vw - 48px))"
      style={{ top: 24 }}
      bodyStyle={{
        maxHeight: "calc(100vh - 160px)",
        overflowY: "auto",
        overflowX: "hidden",
        overscrollBehavior: "contain",
        padding: 16,
      }}
      destroyOnClose
    >
      <Spin spinning={loading} size="large" indicator={<LoadingOutlined />}>
        <Tabs
          activeKey={activeTabKey}
          onChange={(key) => setActiveTabKey(key)}
          items={[{
            key: '1',
            label: 'Details',

            children: <><Form
                {...layout}
                layout={"vertical"}
                validateMessages={validateMessages}
                onFinish={onSavingChanges}
                form={form}
              >
                <Row gutter={24}>
                  <StyledAvatarWrapperColumn span={24}>
                    <Form.Item label="Profile Picture">
                      <StyledUpload
                        listType="picture-card"
                        showUploadList={false}
                        beforeUpload={beforeUpload}
                        onChange={handleChange}
                        className="myaccount-profile-pic"
                      >
                        {profilePic ? (
                          imageLoading ? (
                            <div>
                              <LoadingOutlined />
                            </div>
                          ) : (
                            <StyledProfilePicInnerSection>
                              <img
                                src={profilePic}
                                alt="avatar"
                                style={{
                                  width: "150px",
                                  height: "150px",
                                  borderRadius: "50%",
                                }}
                              />
                              <StyledProfilePicEditSection>
                                <EditOutlined />
                              </StyledProfilePicEditSection>
                            </StyledProfilePicInnerSection>
                          )
                        ) : (
                          <div>
                            {imageLoading ? <LoadingOutlined /> : <PlusOutlined />}
                            <div className="ant-upload-text">Upload Photo</div>
                          </div>
                        )}
                      </StyledUpload>
                    </Form.Item>
                  </StyledAvatarWrapperColumn>
                  <Col xs={24} sm={24} md={24} lg={6} xl={6}>
                    <Form.Item label="Credential" name="credential">
                      <Select
                        id="physician-edit-credential-select"
                        size="large"
                        placeholder="Select"
                      >
                        {
                          doctorCredentials.map((credential) => <Option value={credential.name} key={credential.name}>{credential.name}</Option>)
                        }
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={24} md={24} lg={9} xl={9}>
                    <Form.Item label="First Name" name="firstName">
                      <Input
                        size="large"
                        disabled
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={24} md={24} lg={9} xl={9}>
                    <Form.Item label="Last Name" name="lastName">
                      <Input
                        size="large"
                        disabled
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={24} md={24} lg={24} xl={12}>
                    <Form.Item label="Email" name="email">
                      <Input
                        size="large"
                        disabled
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={24} md={24} lg={24} xl={12}>
                    <Form.Item label="Phone" name="phone">
                      <Input id="physician-edit-phone-input" size="large" />
                    </Form.Item>
                  </Col>
                  <Col span={24}>
                    <Form.Item label="Date of birth" name="DOB">
                      <Input placeholder="Date of birth" disabled />
                    </Form.Item>
                  </Col>
                  <Col span={24}>
                    <Form.Item label="Address Line 1" name="address1">
                      <Input id="physician-edit-address1-input" placeholder="Address Line 1" />
                    </Form.Item>
                  </Col>

                  <Col span={24}>
                    <Form.Item label="Address Line 2 " name="address2">
                      <Input id="physician-edit-address2-input" placeholder="Address Line 2" />
                    </Form.Item>
                  </Col>

                  <Col span={12}>
                    <Form.Item label="City" name="city">
                      <Input id="physician-edit-city-input" placeholder="City" />
                    </Form.Item>
                  </Col>

                  <Col span={12}>
                    <Form.Item label="State" name="state">
                      <Select id="physician-edit-state-select" placeholder="State">
                        {
                          states.map(state => (
                            <Option key={`address-state-${state._id}`} value={state._id}>{state.name}</Option>
                          ))
                        }
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col span={24}>
                    <Form.Item label="Zip Code" name={"zipCode"}>
                      <Input
                        id="physician-edit-zip-code-input"
                        placeholder="Zip Code"
                        style={{ width: "100%" }}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={24}>
                    <Form.Item label="Clients" rules={[{ required: true }]} name="clients">
                      <Select
                        id="physician-edit-clients-select"
                        size="large"
                        mode="multiple"
                        placeholder="Select Clients"
                        showArrow
                      >
                        {
                          clients.map(client => (
                            <Option key={client._id} value={client._id}>{client.name}</Option>
                          ))
                        }
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col span={24}>
                    <Form.Item
                      label="Telemedicine Category"
                      rules={[{ required: true }]}
                      name="teleMedicineCategories"
                    >
                      <Select
                        id="physician-edit-telemedicine-categories-select"
                        size="large"
                        mode="multiple"
                        placeholder="Select Categories"
                        showArrow
                      >
                        {
                          telemedicineCategory.map(category => (
                            <Option key={category._id} value={category._id}>{category.name}</Option>
                          ))
                        }
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col span={24}>
                    <Form.Item label="States" rules={[{ required: true }]} name="states">
                      <Select
                        id="physician-edit-states-select"
                        size="large"
                        mode="multiple"
                        placeholder="Select States"
                        showArrow
                      >
                        {
                          states.map(state => (
                            <Option key={`states-${state._id}`} value={state._id}>{state.name}</Option>
                          ))
                        }
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col span={24}>
                    <Form.Item label="Licensed States" name="licensedStates">
                      <Select
                        id="physician-edit-licensed-states-select"
                        size="large"
                        mode="multiple"
                        placeholder="Select States"
                        showArrow
                      >
                        {
                          states.map(state => (
                            <Option key={`licensedStates-${state._id}`} value={state._id}>{state.name}</Option>
                          ))
                        }
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item label="Zoom User ID" name="zoomUserId">
                      <Input id="physician-edit-zoom-user-id-input" placeholder="" />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      label="Consult Length (m)"
                      initialValue={15}
                      // rules={[value => Number(value) >= 0]}
                      name="consultLengthInMinutes"
                    >
                      <Input id="physician-edit-consult-length-input" type="number" />
                    </Form.Item>
                  </Col>
                  <Col span={24}>
                    <Form.Item
                      label="Employment Status"
                      rules={[{ message: "Please Input your status" }]}
                      name="employmentStatus"
                    >
                      <Select
                        id="physician-edit-employment-status-select"
                        placeholder="Select Employment Status"
                      >
                        {
                          employmentStatus.map(status => (
                            <Option key={status.key} value={status.value}>{status.text}</Option>
                          ))
                        }
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col span={24}>
                    <Form.Item label="Tags" name="tags" >
                      <Select
                        id="physician-edit-tags-select"
                        mode='multiple'
                        style={{ textTransform: 'capitalize' }}
                      >
                        {
                          tags.map(tag => (
                            <Option key={tag} value={tag}>{tag}</Option>
                          ))
                        }
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col span={24}>
                    <Form.Item label="Bio" name="bio">
                      <HugeRTEWrapper height={350} />
                    </Form.Item>
                  </Col>

                  <Col span={24}>
                    <Form.Item
                      style={{ width: "100%" }}
                      label="Active"
                      name="active"
                      valuePropName="checked"
                    >
                      <Switch
                        id="physician-edit-active-switch"
                        disabled={!authorization.isAllowed(userType, "deactivateUser")}
                      />
                    </Form.Item>
                  </Col>
                </Row>
              </Form></>
          }, {
            key: '2',
            label: 'Availability',
            children: <><PhysicianAvailability physicianId={physicianId} /></>
          }, {
            key: '3',
            label: 'Queue Config',

            children: <><PhysicianQueueConfigComponent 
                physician={physician} 
                setPhysician={setPhysician} 
                returnToFirstTab={returnToFirstTab}
              /></>
          }]} />
        <ImageCropperModal
          isVisible={isCropping}
          setIsVisible={setIsCropping}
          imgSrc={croppingImgSrc}
          setImgSrc={(img) => {
            setUpdatePic(img);
            setProfilePic(URL.createObjectURL(img));
          }}
        />
      </Spin >
    </StyledPhysicianEditModal >
  );
};

export default PhysicianDetails;


// TODO: is employment status always == physician group